import { useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  getAllDams,
  getDamById,
  createDam,
  updateDam,
  deleteDam,
} from "../services/damServices";
import { useAppDispatch, useAppSelector } from "../store/hook";
import {
  DAMS_GET_ALL,
  DAMS_GET_BY_NAME,
  DAMS_CREATE,
  DAMS_UPDATE,
  DAMS_DELETE,
} from "../store/dam/action";
import { CsFlexStart } from "@com.ctu.iotlab/ui-library";
import { googleMapLink } from "../utils/gis";

function useDams() {
  const { dams, filterDams } = useAppSelector((state) => state.dam);
  const dispatch = useAppDispatch();

  const [damMarkers, setDamMarkers] = useState([]);
  const [isLoadingDams, setLoadingDams] = useState(false);
  const [selectedDam, setSelectedDam] = useState(null);

  // Lấy toàn bộ danh sách đập
  const fetchDams = async () => {
    try {
      setLoadingDams(true);
      const response = await getAllDams();
      const damData = response.data;

      dispatch(DAMS_GET_ALL({ dams: damData }));

      const markers = damData?.map((dam) => ({
        content: (
          <Box minWidth={"160px"}>
            <Typography pb={1} textTransform={"uppercase"} variant="h6">
              {dam.damName}
            </Typography>
            <CsFlexStart gap={"4px"}>
              <Typography variant="body2" fontWeight={"bold"}>
                Tên sông:
              </Typography>
              <Typography variant="body2">
                {dam.damRiver?.riverName}
              </Typography>
            </CsFlexStart>
            <CsFlexStart gap={"4px"}>
              <Typography variant="body2" fontWeight={"bold"}>
                Trạng thái:
              </Typography>
              <Typography variant="body2">
                {dam?.damCurrentStatus?.damStatusName === "OPEN" ? "Mở" : "Đóng"}
              </Typography>
            </CsFlexStart>
            <CsFlexStart gap={"4px"}>
              <Typography variant="body2" fontWeight={"bold"}>
                Ngày xây dựng:
              </Typography>
              <Typography variant="body2">
                {dam?.damConstructedAt
                  ? `${dam?.damConstructedAt[2]}/${dam?.damConstructedAt[1]}/${dam?.damConstructedAt[0]}`
                  : ""}
              </Typography>
            </CsFlexStart>
            <CsFlexStart gap={"4px"}>
              <Typography variant="body2" fontWeight={"bold"}>
                Chiều cao:
              </Typography>
              <strong>{dam.damHeight}</strong> mét
            </CsFlexStart>
            <CsFlexStart gap={"4px"}>
              <Typography variant="body2" fontWeight={"bold"}>
                Địa điểm:
              </Typography>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={googleMapLink(dam.damLatitude, dam.damLongitude)}
              >
                <Typography variant="body2">Xem vị trí</Typography>
              </a>
            </CsFlexStart>
          </Box>
        ),
        icon: "icons/dam_new.png",
        position: {
          longitude: Number(dam?.damLongitude),
          latitude: Number(dam?.damLatitude),
        },
        type: "dam",
      }));

      setDamMarkers(markers);
    } catch (error) {
      console.error("Error fetching dams:", error);
    } finally {
      setLoadingDams(false);
    }
  };

  // Tìm kiếm theo tên
  const searchDamsByName = (contentSearch: string) => {
    const resultsFound = dams?.filter((item) =>
      (item?.damName + ", " + item.damRiver?.riverName).includes(contentSearch)
    );
    dispatch(DAMS_GET_BY_NAME({ dams: resultsFound }));
  };

  // Lấy chi tiết một đập
  const fetchDamById = async (damId: string) => {
    try {
      const response = await getDamById(damId);
      setSelectedDam(response.data);
    } catch (error) {
      console.error("Error fetching dam by id:", error);
    }
  };

  // Thêm đập mới
  const addDam = async (damData) => {
    try {
      const response = await createDam(damData);
      dispatch(DAMS_CREATE({ dam: response.data }));
      await fetchDams();
    } catch (error) {
      console.error("Error creating dam:", error);
    }
  };

  // Cập nhật thông tin đập
  const editDam = async (damData) => {
    try {
      const response = await updateDam(damData);
      dispatch(DAMS_UPDATE({ dam: response.data }));
      await fetchDams();
    } catch (error) {
      console.error("Error updating dam:", error);
    }
  };

  // Xóa đập
  const removeDam = async (damId: string) => {
    try {
      await deleteDam(damId);
      dispatch(DAMS_DELETE({ damId }));
      await fetchDams();
    } catch (error) {
      console.error("Error deleting dam:", error);
    }
  };

  return {
    dams,
    filterDams,
    damMarkers,
    isLoadingDams,
    selectedDam,
    searchDamsByName,
    fetchDamById,
    addDam,
    editDam,
    removeDam,
    fetchDams
  };
}

export default useDams;
